import { useCallback, useMemo, ChangeEvent, SyntheticEvent } from "react"
import { useTranslation } from "react-i18next"

import Selector from "@/components/form/selector/Selector"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import Toggle from "@/components/form/toggle/Toggle"
import ColorPicker from "@/app/report/components/setting/ColorPicker"
import { colors } from "@/components/colors"
import { CHART_TYPE } from "@/app/report/common/constants"
import {
  usePdfBuilderStore,
  selectSelectedItem,
  selectCurrentPageConfig,
} from "@/stores/pdfBuilder"
import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import {
  coerceVerticalChartType,
  getTemplateGraphCharts,
  resolveGraphOrientation,
  resolveGraphShowAxis,
  resolveTemplateGraphChart,
  VERTICAL_CHART_TYPES,
} from "@/app/report/common/sections/graph"
import { getTemplateTrendCharts } from "@/app/report/common/sections/trendGraph"
import {
  VERTICAL_PLAYERS_PER_PAGE_DEFAULT,
  VERTICAL_PLAYERS_PER_PAGE_MAX_LANDSCAPE,
  VERTICAL_PLAYERS_PER_PAGE_MAX_PORTRAIT,
} from "@/app/report/common/chart/verticalPlayerChart"

import type { ChartOptionsPanelProps } from "./types"
import {
  DecimalPlacesSelector,
  MetricSelector,
  MetricSeriesRows,
  SortSelectors,
  type SortCategory,
} from "./SharedWidgets"
import { MAX_CHART_SERIES } from "@/app/report/pdf/charts/BuilderD3Chart"

const DEFAULT_METRIC_COLORS = [colors.blue[900], colors.green[700], colors.red[500]]

type ChartOption = {
  value: string
  label: string
}

/**
 * Bar/Line/Combo 의 축 설정(단위·소수 자릿수·수동 스케일)·값 표시·정렬은 Report Filters 에서 고른
 * 리포트 템플릿의 Graph 차트 설정을 따른다 (일감 #75, 4단계). 지표는 처음 추가될 때 그 차트의 지표·색을
 * 가져와 선택된 상태로 두고, 여기서 더하거나 뺀다 (결정 2026-09-23). 색·가로/세로·범례·자동 크기는
 * 빌더 고유 옵션이다. 그 외 항목을 바꾸려면 Settings > Report 의 템플릿에서 고친다.
 */
export default function BarChartOptions(props: ChartOptionsPanelProps) {
  const { t } = useTranslation()
  const { item, onDataSourceChange, onOptionChange, metricOptions, translateKey } = props
  const { reportTemplate, reportKind } = usePdfDataContext()

  // 트렌드는 템플릿의 액티비티 그래프·선수 그래프 섹션에서 차트를 찾는다 (2026-10-02 요청)
  const isTrend = reportKind === "trend"
  const isActivityRows = item.chartType === "activityGraph"
  const templateCharts = useMemo(
    () =>
      isTrend
        ? getTemplateTrendCharts(reportTemplate, isActivityRows ? "activity" : "player")
        : getTemplateGraphCharts(reportTemplate),
    [isTrend, isActivityRows, reportTemplate],
  )
  const currentChart = useMemo(
    () =>
      resolveTemplateGraphChart(
        templateCharts,
        item.dataSource.sectionIndex,
        item.dataSource.chartIndex,
      ),
    [templateCharts, item.dataSource.sectionIndex, item.dataSource.chartIndex],
  )
  const chartOptions: ChartOption[] = useMemo(
    () =>
      templateCharts.map((chart, order) => ({
        value: `${chart.sectionIndex}:${chart.chartIndex}`,
        label: chart.settings.title
          ? t(chart.settings.title)
          : `${t("chart", "Chart")} ${order + 1}`,
      })),
    [templateCharts, t],
  )

  // Chart by Player(combo)는 지표 최대 3개 (2026-09-28 요청)
  const isChartByPlayer = item.chartType === "combo" || item.chartType === "activityGraph"
  const templateMetrics = useMemo(() => {
    const keys = currentChart?.settings.yAxis.map((y) => y.metric) ?? []

    return isChartByPlayer ? keys.slice(0, MAX_CHART_SERIES) : keys
  }, [currentChart, isChartByPlayer])
  const metrics = isChartByPlayer
    ? (item.dataSource.metrics ?? templateMetrics).slice(0, MAX_CHART_SERIES)
    : (item.dataSource.metrics ?? templateMetrics)
  const metricColors = item.options.colors ?? []

  // 차트 방향 — 템플릿 Graph 설정이 기본, 아이템별 덮어쓰기 (#185). 옛 Bar 의 "Column Layout: horizontal" 은 세로로 읽는다
  const orientation =
    item.options.chartOrientation ??
    (item.chartType === "bar" && item.options.orientation === "horizontal"
      ? "vertical"
      : resolveGraphOrientation(currentChart?.settings))
  const isLandscape =
    usePdfBuilderStore((s) => selectCurrentPageConfig(s)?.orientation) === "landscape"
  const playersPerPageMax = isLandscape
    ? VERTICAL_PLAYERS_PER_PAGE_MAX_LANDSCAPE
    : VERTICAL_PLAYERS_PER_PAGE_MAX_PORTRAIT
  const playersPerPage = Math.min(
    playersPerPageMax,
    item.options.playersPerPage ?? VERTICAL_PLAYERS_PER_PAGE_DEFAULT,
  )
  const setPlayersPerPage = (value: number) =>
    onOptionChange("playersPerPage", Math.max(1, Math.min(playersPerPageMax, value)))
  const showXAxis = item.options.showXAxis ?? resolveGraphShowAxis(currentChart?.settings)

  // 값 표시 위치·정렬 — 템플릿이 기본, 아이템별 덮어쓰기 (2026-09-30 요청: 빌더에서만)
  const dataLabelOptions = [
    { value: "hideValues", label: t("hideValues", "Hide values") },
    { value: "insidePlots", label: t("insidePlots", "In chart") },
    { value: "abovePlots", label: t("abovePlots", "Above plots") },
    { value: "separateTable", label: t("separateTable", "Separate table") },
  ]
  const dataLabel = item.options.dataLabel ?? currentChart?.settings.dataLabel ?? "hideValues"
  const sortCategories: SortCategory[] = [
    // 정렬 안 한 기본 상태 — 템플릿 정렬(없으면 등번호 순)을 그대로 따른다 (2026-10-01 요청)
    {
      value: "default",
      label: t("sortDefaultOrder", "Default"),
      directions: [{ value: "asc" as const, label: t("sortDefaultOrder", "Default") }],
    },
    ...metrics.map((metricKey) => ({
      value: metricKey,
      label: translateKey(metricKey),
      directions: [
        { value: "asc" as const, label: t("lowToHigh", "Low to High") },
        { value: "desc" as const, label: t("highToLow", "High to Low") },
      ],
    })),
    // 한 줄이 액티비티면 선수 기준(이름·포지션) 대신 날짜·액티비티 이름으로 (2026-10-02 요청)
    ...(isActivityRows
      ? [
          {
            value: "date",
            label: t("date", "Date"),
            directions: [
              { value: "asc" as const, label: t("oldToNew", "Old to New") },
              { value: "desc" as const, label: t("newToOld", "New to Old") },
            ],
          },
          {
            value: "activityName",
            label: t("activityName", "Activity name"),
            directions: [
              { value: "asc" as const, label: t("aToZ", "A - Z") },
              { value: "desc" as const, label: t("zToA", "Z - A") },
            ],
          },
        ]
      : [
          {
            value: "playerName",
            label: t("playerName", "Player name"),
            directions: [
              { value: "asc" as const, label: t("aToZ", "A - Z") },
              { value: "desc" as const, label: t("zToA", "Z - A") },
            ],
          },
          {
            value: "position",
            label: t("position", "Position"),
            directions: [
              { value: "asc" as const, label: "FW - GK" },
              { value: "desc" as const, label: "GK - FW" },
            ],
          },
        ]),
  ]
  const sortValue = item.options.sortKey
    ? { key: item.options.sortKey, order: item.options.sortOrder ?? ("asc" as const) }
    : { key: "default", order: "asc" as const }

  const getMetricColor = (index: number) =>
    metricColors[index] ??
    currentChart?.settings.yAxis.find((y) => y.metric === metrics[index])?.color ??
    DEFAULT_METRIC_COLORS[index % DEFAULT_METRIC_COLORS.length]

  const getLatestColors = (): string[] => {
    const state = usePdfBuilderStore.getState()
    const latest = selectSelectedItem(state)

    return [...(latest?.options.colors ?? [])]
  }

  const getLatestMetrics = (): string[] => {
    const state = usePdfBuilderStore.getState()
    const latest = selectSelectedItem(state)

    return latest?.dataSource.metrics ?? templateMetrics
  }

  const handleAddMetric = useCallback(
    (metricKey: string) => {
      const currentMetrics = getLatestMetrics()
      const currentColors = getLatestColors()
      // 기존 지표마다 색이 하나씩 있게 채운 뒤, 새 지표의 기본 색을 붙인다
      while (currentColors.length < currentMetrics.length) {
        currentColors.push(
          DEFAULT_METRIC_COLORS[currentColors.length % DEFAULT_METRIC_COLORS.length],
        )
      }
      currentColors.push(DEFAULT_METRIC_COLORS[currentColors.length % DEFAULT_METRIC_COLORS.length])
      onDataSourceChange("metrics", [...currentMetrics, metricKey])
      onOptionChange("colors", currentColors)
    },
    [onDataSourceChange, onOptionChange, templateMetrics],
  )

  const handleRemoveMetric = useCallback(
    (metricKey: string) => {
      const currentMetrics = getLatestMetrics()
      const removeIndex = currentMetrics.indexOf(metricKey)
      const newMetrics = currentMetrics.filter((k) => k !== metricKey)
      const newColors = getLatestColors()
      if (removeIndex >= 0 && removeIndex < newColors.length) {
        newColors.splice(removeIndex, 1)
      }
      onDataSourceChange("metrics", newMetrics)
      onOptionChange("colors", newColors)
    },
    [onDataSourceChange, onOptionChange, templateMetrics],
  )

  const handleMetricColorChange = useCallback(
    (index: number, color: string) => {
      const newColors = getLatestColors()
      while (newColors.length <= index) {
        newColors.push(getMetricColor(newColors.length))
      }
      newColors[index] = color
      onOptionChange("colors", newColors)
    },
    [onOptionChange, metrics, currentChart],
  )

  return (
    <>
      {/* 어느 차트를 따를지 — 템플릿에 차트가 둘 이상일 때만 고른다 */}
      {templateCharts.length > 1 && (
        <div className="PropertyPanel__field">
          <Selector
            slotProps={{ popper: { className: "PropertyPanel__menu" } }}
            label={t("chart", "Chart")}
            options={chartOptions}
            value={
              chartOptions.find(
                (option) =>
                  option.value === `${currentChart?.sectionIndex}:${currentChart?.chartIndex}`,
              ) || chartOptions[0]
            }
            onChange={(_: SyntheticEvent, newVal: ChartOption | null) => {
              if (!newVal) {
                return
              }
              const [sectionIndex, chartIndex] = newVal.value.split(":").map(Number)
              onDataSourceChange("sectionIndex", sectionIndex)
              onDataSourceChange("chartIndex", chartIndex)
              // 따르는 차트가 바뀌면 지표·색도 그 차트의 것으로 다시 가져온다
              const nextChart = templateCharts.find(
                (chart) => chart.sectionIndex === sectionIndex && chart.chartIndex === chartIndex,
              )
              if (nextChart) {
                onDataSourceChange(
                  "metrics",
                  nextChart.settings.yAxis.map((y) => y.metric),
                )
                onOptionChange(
                  "colors",
                  nextChart.settings.yAxis.map((y) => y.color),
                )
              }
            }}
            placeholder={t("select")}
          />
        </div>
      )}
      {templateCharts.length === 0 && (
        <div className="PropertyPanel__field">
          <span className="PropertyPanel__field-desc">
            {t("graphNotInTemplate", "The selected report template has no Graph section")}
          </span>
        </div>
      )}

      {/* 차트 방향 — 가로(선수가 X축) / 세로(선수 세로 나열). 세로면 장당 선수 수로 페이지를 나눈다 (#185) */}
      <div className="PropertyPanel__field">
        <label>{t("orientation", "Orientation")}</label>
        <RadioGroup
          direction="row"
          items={[
            { label: t("orientationHorizontal", "Horizontal"), value: "horizontal" },
            { label: t("orientationVertical", "Vertical"), value: "vertical" },
          ]}
          value={orientation}
          onChange={(_: ChangeEvent<HTMLInputElement>, value?: string) => {
            if (value) {
              onOptionChange("chartOrientation", value)
            }
          }}
        />
      </div>

      {orientation === "vertical" && (
        <div className="PropertyPanel__field PropertyPanel__field--toggle">
          <Toggle
            checked={showXAxis}
            onChange={(e) => onOptionChange("showXAxis", e.target.checked)}
          />
          <label>{t("showXAxis", "Show X axis")}</label>
        </div>
      )}

      {currentChart && (
        <MetricSelector
          label={t("metrics", "Metrics")}
          selectedMetrics={metrics}
          metricOptions={metricOptions}
          onAdd={handleAddMetric}
          onRemove={handleRemoveMetric}
          translateKey={translateKey}
          maxCount={isChartByPlayer ? MAX_CHART_SERIES : undefined}
        />
      )}

      {/* Chart by Player — 지표마다 [이름, 차트 종류, 색]. 종류는 템플릿 축 종류가 기본이고 여기서 바꾼 것만 아이템에 저장 */}
      {isChartByPlayer && (
        <MetricSeriesRows
          label={t("series", "Series")}
          metrics={metrics}
          getChartType={(metricKey) => {
            const chartType =
              item.options.metricChartTypes?.[metricKey] ??
              currentChart?.settings.yAxis.find((y) => y.metric === metricKey)?.chartType ??
              CHART_TYPE.BAR

            // 세로 차트는 막대·누적 막대만 — 그 외는 막대로 보인다 (2026-09-30 결정)
            return orientation === "vertical" ? coerceVerticalChartType(chartType) : chartType
          }}
          allowedTypes={orientation === "vertical" ? VERTICAL_CHART_TYPES : undefined}
          // 최대값 — 아이템 스냅샷(null 은 자동) > 템플릿 수동 스케일 (2026-10-01 결정)
          getScale={(metricKey) => {
            const entry = item.options.metricScales?.[metricKey]
            if (entry !== undefined) {
              return entry
            }
            const axis = currentChart?.settings.yAxis.find((y) => y.metric === metricKey)

            return axis?.manualScaleActive && axis.manualScaleValue != null
              ? axis.manualScaleValue
              : null
          }}
          onScaleChange={(metricKey, value) =>
            onOptionChange("metricScales", {
              ...(item.options.metricScales ?? {}),
              [metricKey]: value,
            })
          }
          getColor={getMetricColor}
          onChartTypeChange={(metricKey, chartType) => {
            const latest = selectSelectedItem(usePdfBuilderStore.getState())
            onOptionChange("metricChartTypes", {
              ...(latest?.options.metricChartTypes ?? {}),
              [metricKey]: chartType,
            })
          }}
          onColorChange={handleMetricColorChange}
          translateKey={translateKey}
        />
      )}

      {/* 정렬 — 기준(기본·지표·이름·포지션) · 방향, 작은 select 둘을 가로로 */}
      <SortSelectors
        label={t("sort", "Sort")}
        categories={sortCategories}
        value={sortValue}
        onChange={(next) => {
          onOptionChange("sortKey", next.key)
          onOptionChange("sortOrder", next.order)
        }}
      />

      {/* 값 표시 위치 — 없음 / 그래프 가운데 / 그래프 위 / 모아서 보기 */}
      <div className="PropertyPanel__field">
        <Selector
          slotProps={{ popper: { className: "PropertyPanel__menu" } }}
          label={t("dataLabels", "Data labels")}
          options={dataLabelOptions}
          value={dataLabelOptions.find((o) => o.value === dataLabel) || dataLabelOptions[0]}
          onChange={(_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
            if (newVal) {
              onOptionChange("dataLabel", newVal.value)
            }
          }}
          placeholder={t("select")}
        />
      </div>

      {/* 소수점 — 아이템 값 > 템플릿 값 > 0.1 (2026-10-01 결정) */}
      <DecimalPlacesSelector
        value={item.options.decimalPlaces ?? (currentChart?.settings.decimalPlace || 1)}
        onChange={(value) => onOptionChange("decimalPlaces", value)}
      />

      {/* 장당 선수 수 — Layout 이 Manual 일 때만. Auto 는 표처럼 남은 페이지에 들어가는 만큼 스스로 나눈다 (2026-10-02 요청) */}
      {orientation === "vertical" && item.options.layoutMode === "manual" && (
        <div className="PropertyPanel__field">
          <label>{t("playersPerPage", "Players per page")}</label>
          <div className="PropertyPanel__stepper">
            <input
              className="PropertyPanel__stepper-input"
              type="number"
              min={1}
              max={playersPerPageMax}
              value={playersPerPage}
              onChange={(e) => setPlayersPerPage(parseInt(e.target.value) || 1)}
            />
            <button
              className="PropertyPanel__stepper-btn"
              onClick={() => setPlayersPerPage(playersPerPage - 1)}
              disabled={playersPerPage <= 1}>
              −
            </button>
            <button
              className="PropertyPanel__stepper-btn"
              onClick={() => setPlayersPerPage(playersPerPage + 1)}
              disabled={playersPerPage >= playersPerPageMax}>
              +
            </button>
          </div>
        </div>
      )}

      {/* Chart Color (Bar/Line/Point 레거시 아이템) — 템플릿 색이 기본, 아이템별로 바꿀 수 있다 */}
      {!isChartByPlayer && metrics.length > 0 && (
        <div className="PropertyPanel__field">
          <label>{t("chartColor", "Chart Color")}</label>
          <div className="PropertyPanel__color-swatches">
            {metrics.map((key, index) => (
              <ColorPicker
                key={key}
                color={getMetricColor(index)}
                setColor={(color) => handleMetricColorChange(index, color)}
                hideLabel
              />
            ))}
          </div>
        </div>
      )}

      {/* Show Legend */}
      <div className="PropertyPanel__field PropertyPanel__field--toggle">
        <Toggle
          checked={item.options.showLegend ?? true}
          onChange={(e) => onOptionChange("showLegend", e.target.checked)}
        />
        <label>{t("showLegend", "Show Legend")}</label>
      </div>
    </>
  )
}
